import { cssToken } from "../../utilities/cssToken";

/**
 * Series colouring (ASB-4377, design §7.4 and A.2 "Roles in charts"): meaning,
 * not colour, in the manifest. An enum column can map values to roles; the
 * renderer colours by value, so a category keeps its colour whatever the
 * range or filter. Several values that share a role get distinct shades of
 * that role's colour family: the N-th value with a role, counting in the
 * order the manifest's `roles` map lists them, takes shade N
 * (--as-chart-role-<role>-N, wrapping after ROLE_SHADES). The assignment reads
 * the map, not the rows, so it never shifts with the range. Values with no
 * role take the categorical tokens --as-chart-1…6 in a fixed order (sorted as
 * strings). The role set is closed; an unknown role is treated as unmapped.
 *
 * A `muted` series is hidden by default in a trend chart (the legend toggles
 * it back on): "not checked" is most of every bar and would hide the rest.
 *
 * Colours resolve through cssToken(), so tests pass their own resolver and a
 * chart without a DOM falls back to Chart.js's default rather than a literal.
 */
export const ROLES = ["good", "warning", "critical", "muted", "neutral"];
export const ROLE_SHADES = 3;
export const HIDDEN_ROLES = ["muted"];

/** The role's base alias (shade 1 of its family). */
export const ROLE_TOKENS = {
	good: "--as-chart-role-good",
	warning: "--as-chart-role-warning",
	critical: "--as-chart-role-critical",
	muted: "--as-chart-role-muted",
	neutral: "--as-chart-role-neutral",
};

export const CATEGORICAL_TOKENS = [
	"--as-chart-1",
	"--as-chart-2",
	"--as-chart-3",
	"--as-chart-4",
	"--as-chart-5",
	"--as-chart-6",
];

const asKey = (value) => String(value ?? "");
const compare = (a, b) => asKey(a).localeCompare(asKey(b), "en");

/** The role a value maps to, or null when the column maps none (or an unknown one). */
export function roleFor(value, roles) {
	const role = roles ? roles[asKey(value)] : undefined;
	return ROLES.includes(role) ? role : null;
}

/** Where a value stands among the values that share its role, in the roles map's order (0-based); -1 when unmapped. */
export function roleOrdinal(value, roles) {
	const role = roleFor(value, roles);
	if (!role) return -1;
	const key = asKey(value);
	let n = 0;
	for (const other of Object.keys(roles)) {
		if (other === key) return n;
		if (roles[other] === role) n += 1;
	}
	return 0;
}

/** The shade token for a role and ordinal: --as-chart-role-warning-2 for the second warning value. */
export const roleShadeToken = (role, ordinal) =>
	`${ROLE_TOKENS[role]}-${(Math.max(0, ordinal) % ROLE_SHADES) + 1}`;

/** The token for one value in a roles map (its role's shade), or null when unmapped. Charts and table dots both use it. */
export function roleToken(value, roles) {
	const role = roleFor(value, roles);
	return role ? roleShadeToken(role, roleOrdinal(value, roles)) : null;
}

/** Whether a value's series starts hidden in a trend chart (muted role). */
export const hiddenByDefault = (value, roles) => HIDDEN_ROLES.includes(roleFor(value, roles));

/**
 * Distinct series values in display order: the values with a role first, in
 * the order the manifest lists the roles, then the rest sorted as strings.
 */
export function orderSeries(values, roles) {
	const distinct = [];
	const seen = new Set();
	for (const v of values) {
		const k = asKey(v);
		if (seen.has(k)) continue;
		seen.add(k);
		distinct.push(k);
	}
	const roleOrder = Object.keys(roles ?? {});
	const mapped = distinct
		.filter((v) => roleFor(v, roles))
		.sort((a, b) => roleOrder.indexOf(a) - roleOrder.indexOf(b));
	const unmapped = distinct.filter((v) => !roleFor(v, roles)).sort(compare);
	return [...mapped, ...unmapped];
}

/**
 * The token (and its resolved colour) for every series value, as a Map keyed by
 * the value as a string: `{ token, color, role, hidden }`. Mapped values take
 * their role's shade; the others take --as-chart-N in their fixed order,
 * wrapping after six.
 */
export function assignSeriesColors(values, roles, resolve = cssToken) {
	const out = new Map();
	const ordered = orderSeries(values, roles);
	let next = 0;
	for (const v of ordered) {
		const role = roleFor(v, roles);
		const token = role
			? roleToken(v, roles)
			: CATEGORICAL_TOKENS[next++ % CATEGORICAL_TOKENS.length];
		out.set(v, { token, color: resolve(token), role, hidden: hiddenByDefault(v, roles) });
	}
	return out;
}

/** The N-th categorical token, for series that are columns rather than values. */
export const categoricalToken = (index) => CATEGORICAL_TOKENS[index % CATEGORICAL_TOKENS.length];
